import React from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, PlaneIcon, BuildingIcon, HeartIcon, LandmarkIcon } from 'lucide-react';
import { JaliDivider } from './JaliDivider';
import { useLanguage } from '../lib/LanguageContext';
export function WhyCancun() {
  const {
    t,
    tArray
  } = useLanguage();
  const pointsData = tArray<{
    title: string;
    desc: string;
  }>('whyCancun.points');
  const icons = [<HeartIcon className="w-5 h-5" data-id="element-209" />, <PlaneIcon className="w-5 h-5" data-id="element-210" />, <BuildingIcon className="w-5 h-5" data-id="element-211" />, <MapPinIcon className="w-5 h-5" data-id="element-212" />, <LandmarkIcon className="w-5 h-5" data-id="element-213" />];
  const points = pointsData.map((p, i) => ({
    ...p,
    icon: icons[i % icons.length]
  }));
  // City positions spread across portrait viewBox (420 x 530)
  const flightRoutes = [
  // North America — spread vertically
  {
    city: 'Vancouver',
    x: 80,
    y: 70,
    labelAnchor: 'end' as const
  }, {
    city: 'Calgary',
    x: 130,
    y: 55,
    labelAnchor: 'end' as const
  }, {
    city: 'Toronto',
    x: 300,
    y: 95,
    labelAnchor: 'start' as const
  }, {
    city: 'New York',
    x: 330,
    y: 140,
    labelAnchor: 'start' as const
  }, {
    city: 'Chicago',
    x: 250,
    y: 115,
    labelAnchor: 'end' as const
  }, {
    city: 'Los Angeles',
    x: 85,
    y: 205,
    labelAnchor: 'end' as const
  }, {
    city: 'Dallas',
    x: 200,
    y: 215,
    labelAnchor: 'end' as const
  }, {
    city: 'Houston',
    x: 210,
    y: 245,
    labelAnchor: 'end' as const
  }, {
    city: 'Miami',
    x: 310,
    y: 265,
    labelAnchor: 'start' as const
  }, {
    city: 'Mexico City',
    x: 165,
    y: 395,
    labelAnchor: 'end' as const
  },
  // International (entering from edges)
  {
    city: 'London',
    x: 400,
    y: 70,
    labelAnchor: 'end' as const,
    isEdge: true
  }, {
    city: 'Dubai',
    x: 400,
    y: 195,
    labelAnchor: 'end' as const,
    isEdge: true
  }, {
    city: 'Delhi',
    x: 400,
    y: 290,
    labelAnchor: 'end' as const,
    isEdge: true
  }, {
    city: 'Amritsar',
    x: 400,
    y: 345,
    labelAnchor: 'end' as const,
    isEdge: true
  }, {
    city: 'São Paulo',
    x: 40,
    y: 470,
    labelAnchor: 'start' as const,
    isEdge: true
  }];
  // Cancun position — centered in the lower-middle
  const cancun = {
    x: 240,
    y: 330
  };
  return <section className="py-24 bg-sandstone relative overflow-hidden" data-id="element-214">
      <JaliDivider className="absolute top-0 left-0" data-id="element-215" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10" data-id="element-216">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-217">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.8
        }} data-id="element-218">
            <h2 className="text-3xl md:text-5xl font-heading text-navy mb-8" data-id="element-219">
              {t('whyCancun.title')}
            </h2>
            <p className="text-navy/70 text-lg leading-relaxed mb-10" data-id="element-220">
              {t('whyCancun.description')}
            </p>

            <ul className="space-y-6" data-id="element-221">
              {points.map((point, idx) => <motion.li key={idx} initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: idx * 0.1
            }} className="flex items-start" data-id="element-222">
                  <div className="mt-1 mr-4 text-saffron bg-ivory p-2 rounded-full shadow-sm" data-id="element-223">
                    {point.icon}
                  </div>
                  <div data-id="element-224">
                    <h4 className="font-heading text-xl text-navy mb-1" data-id="element-225">
                      {point.title}
                    </h4>
                    <p className="text-navy/60" data-id="element-226">{point.desc}</p>
                  </div>
                </motion.li>)}
            </ul>
          </motion.div>

          {/* Stylized Map — Cancun's Strategic Location */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 1
        }} className="relative h-[600px] rounded-sm overflow-hidden hidden lg:block" data-id="element-227">
            <div className="absolute inset-0 bg-marble border border-gold/20 flex items-center justify-center p-4" data-id="element-228">
              <svg viewBox="0 0 420 530" className="w-full h-full" preserveAspectRatio="xMidYMid meet" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-229">
                {/* Subtle grid background */}
                <defs data-id="element-230">
                  <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse" data-id="element-231">
                    <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#C9A84C" strokeWidth="0.15" opacity="0.3" data-id="element-232" />
                  </pattern>
                  <radialGradient id="cancunGlow" cx="50%" cy="50%" r="50%" data-id="element-233">
                    <stop offset="0%" stopColor="#D4A853" stopOpacity="0.4" data-id="element-234" />
                    <stop offset="100%" stopColor="#D4A853" stopOpacity="0" data-id="element-235" />
                  </radialGradient>
                  <marker id="arrow" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="4" markerHeight="4" orient="auto-start-reverse" data-id="element-236">
                    <path d="M 0 0 L 10 5 L 0 10 z" fill="#C9A84C" opacity="0.8" data-id="element-237" />
                  </marker>
                </defs>
                <rect width="420" height="530" fill="url(#grid)" data-id="element-238" />

                {/* Coastline outlines — spread for portrait layout */}
                {/* USA/Canada West Coast */}
                <path d="M 50 20 L 60 60 L 70 110 L 80 160 L 90 210 L 100 250" stroke="#1B2A4A" strokeWidth="1.5" opacity="0.15" fill="none" strokeLinecap="round" data-id="element-239" />
                {/* USA/Canada East Coast */}
                <path d="M 340 20 L 325 70 L 330 120 L 315 170 L 300 220 L 290 260" stroke="#1B2A4A" strokeWidth="1.5" opacity="0.15" fill="none" strokeLinecap="round" data-id="element-240" />
                {/* Gulf coast */}
                <path d="M 290 260 L 265 250 L 240 240 L 215 250 L 185 260 L 155 280 L 140 300" stroke="#1B2A4A" strokeWidth="1.5" opacity="0.15" fill="none" strokeLinecap="round" data-id="element-241" />
                {/* Mexico West Coast */}
                <path d="M 100 250 L 115 290 L 130 300 L 140 330 L 150 360 L 160 380 L 175 390 L 190 375 L 205 355 L 220 340 L 235 335 L 245 330" stroke="#1B2A4A" strokeWidth="1.5" opacity="0.15" fill="none" strokeLinecap="round" data-id="element-242" />
                {/* Yucatan peninsula */}
                <path d="M 175 390 L 195 375 L 215 360 L 230 350 L 240 340 L 245 330 L 240 315 L 225 300 L 210 285" stroke="#1B2A4A" strokeWidth="1.5" opacity="0.15" fill="none" strokeLinecap="round" data-id="element-243" />
                {/* Cuba */}
                <path d="M 260 350 L 280 345 L 300 340 L 320 345 L 330 350" stroke="#1B2A4A" strokeWidth="1.2" opacity="0.12" fill="none" strokeLinecap="round" data-id="element-244" />
                {/* Caribbean islands */}
                <circle cx="355" cy="360" r="3" fill="#1B2A4A" opacity="0.08" data-id="element-245" />
                <circle cx="365" cy="380" r="2.5" fill="#1B2A4A" opacity="0.08" data-id="element-246" />
                <circle cx="370" cy="398" r="2" fill="#1B2A4A" opacity="0.08" data-id="element-247" />

                {/* Ocean labels */}
                <text x="300" y="430" fill="#5B8A8A" opacity="0.25" fontSize="11" fontFamily="Inter, sans-serif" fontStyle="italic" letterSpacing="3" data-id="element-248">
                  Caribbean Sea
                </text>
                <text x="60" y="360" fill="#5B8A8A" opacity="0.2" fontSize="10" fontFamily="Inter, sans-serif" fontStyle="italic" letterSpacing="2" data-id="element-249">
                  Pacific
                </text>

                {/* Flight route lines (curved) */}
                {flightRoutes.map((route, idx) => {
                const midX = (route.x + cancun.x) / 2;
                const midY = (route.y + cancun.y) / 2;
                const cpX = midX + (route.y - cancun.y) * 0.2;
                const cpY = midY - (route.x - cancun.x) * 0.2;
                return <motion.g key={route.city} data-id="element-250">
                      <motion.path d={`M ${route.x} ${route.y} Q ${cpX} ${cpY} ${cancun.x} ${cancun.y}`} stroke="#C9A84C" strokeWidth="1" strokeDasharray="4 4" fill="none" opacity="0.4" markerMid="url(#arrow)" initial={{
                    pathLength: 0,
                    opacity: 0
                  }} whileInView={{
                    pathLength: 1,
                    opacity: 0.4
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 1.5,
                    delay: 0.2 + idx * 0.1
                  }} data-id="element-251" />
                      <motion.circle r="2" fill="#D4A853" initial={{
                    opacity: 0
                  }} whileInView={{
                    opacity: [0, 1, 0]
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 2,
                    delay: 0.5 + idx * 0.1,
                    repeat: Infinity,
                    repeatDelay: 3
                  }} data-id="element-252">
                        <animateMotion path={`M ${route.x} ${route.y} Q ${cpX} ${cpY} ${cancun.x} ${cancun.y}`} dur="2s" repeatCount="indefinite" data-id="element-253" />
                      </motion.circle>
                      {!route.isEdge && <motion.circle cx={route.x} cy={route.y} r="3" fill="#1B2A4A" opacity="0.4" initial={{
                    scale: 0
                  }} whileInView={{
                    scale: 1
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 0.4,
                    delay: 0.1 + idx * 0.1
                  }} data-id="element-254" />}
                      <motion.text x={route.labelAnchor === 'end' ? route.x - 8 : route.x + 8} y={route.y + 3} textAnchor={route.labelAnchor} fill="#1B2A4A" opacity="0.6" fontSize="9" fontFamily="Inter, sans-serif" fontWeight="500" letterSpacing="0.5" initial={{
                    opacity: 0
                  }} whileInView={{
                    opacity: 0.6
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 0.5,
                    delay: 0.3 + idx * 0.1
                  }} data-id="element-255">
                        {route.city}
                      </motion.text>
                    </motion.g>;
              })}

                {/* Cancun — glowing marker */}
                <motion.circle cx={cancun.x} cy={cancun.y} r="40" fill="url(#cancunGlow)" initial={{
                scale: 0
              }} whileInView={{
                scale: 1
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.8,
                delay: 0.5
              }} data-id="element-256" />
                <motion.circle cx={cancun.x} cy={cancun.y} r="16" fill="#D4A853" opacity="0.15" initial={{
                scale: 0
              }} whileInView={{
                scale: [0, 1.5, 1]
              }} viewport={{
                once: true
              }} transition={{
                duration: 1,
                delay: 0.6
              }} data-id="element-257" />
                {/* Gold dot */}
                <motion.circle cx={cancun.x} cy={cancun.y} r="14" fill="#D4A853" initial={{
                scale: 0
              }} whileInView={{
                scale: 1
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: 0.7
              }} data-id="element-258" />
                {/* Subtle ring */}
                <motion.circle cx={cancun.x} cy={cancun.y} r="16" stroke="#D4A853" strokeWidth="0.5" fill="none" opacity="0.5" initial={{
                scale: 0
              }} whileInView={{
                scale: 1
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 0.75
              }} data-id="element-259" />

                {/* Ik Onkar ੴ on the gold dot — white */}
                <text x={cancun.x} y={cancun.y + 5} textAnchor="middle" dominantBaseline="central" fill="#FFFFFF" fontSize="16" fontFamily="'Noto Sans Gurmukhi', sans-serif" fontWeight="400" data-id="element-260">
                  ੴ
                </text>

                {/* Cancun label */}
                <motion.g initial={{
                opacity: 0,
                y: 5
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 1
              }} data-id="element-261">
                  <text x={cancun.x} y={cancun.y + 28} textAnchor="middle" fill="#1B2A4A" fontSize="16" fontFamily="Playfair Display, serif" fontWeight="600" letterSpacing="2" data-id="element-262">
                    CANCÚN
                  </text>
                </motion.g>

                {/* Compass rose */}
                <g opacity="0.15" transform="translate(380, 40)" data-id="element-263">
                  <line x1="0" y1="-12" x2="0" y2="12" stroke="#1B2A4A" strokeWidth="0.8" data-id="element-264" />
                  <line x1="-12" y1="0" x2="12" y2="0" stroke="#1B2A4A" strokeWidth="0.8" data-id="element-265" />
                  <text x="0" y="-16" textAnchor="middle" fill="#1B2A4A" fontSize="8" fontFamily="Inter, sans-serif" fontWeight="600" data-id="element-266">
                    N
                  </text>
                </g>
              </svg>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}